import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Form, Icon, Label, List, Loader, Segment, TextArea } from 'semantic-ui-react';
import moment from 'moment';
import { listComments, createComment, updateComment, deleteComment, getCommentUploadUrl } from '../../services/Comments';
import { reportContent } from '../../services/Posts';
import { Comment } from '../../types/Comment';
import { formatDateWithTZ } from '../../../utils/timezone';
import sendToast from '../../../utils/Toast';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import ReportContentModal from '../ReportContentModal';
import LoadMore from '../../../utils/LoadMore';
import useActiveResident from '../../hooks/useActiveResident';
import Avatar from '../../../components/components/Avatar';
import { ALLOWED_TYPES, MAX_FILE_SIZE } from '../../utils/fileValidation';
import { getResidentDisplayFirstName, getResidentDisplayImage, getResidentDisplayLastName } from '../../utils/residentIdentity';

// Request/service-instance comment threads are short (a handful of comments per
// order or event) and fetch everything in one shot. Post comments can grow much
// larger, so they alone get real Load-More pagination — see POST_PAGE_SIZE below.
const PAGE_SIZE = 50;
const POST_PAGE_SIZE = 8;
const MAX_COMMENT_LENGTH = 500;

const COMMENT_TEXTAREA_STYLE = {
    resize: 'vertical' as const,
    minHeight: 96,
    width: '100%',
    boxSizing: 'border-box' as const,
};
const COMMENT_TEXTAREA_ROWS = 3;

export interface OrderCommentsSectionProps {
    /** Only render when true. */
    commentsEnabled: boolean;
    facilityId: string;
    /** Existing orders flow target. */
    requestInstanceId?: string;
    /** Reusable target for service-instance comments (calendar/menu). */
    serviceInstanceId?: string;
    /** Reusable target for post comments. */
    postId?: string;
    /** Optional explicit target type; inferred from provided id when omitted. */
    targetType?: 'requestInstance' | 'serviceInstance' | 'post';
    registrantId: string;
    facilityTimezone: string;
    /** Notified with the current comment count whenever the thread (re)loads. */
    onCountChange?: (count: number) => void;
    /**
     * 'card' — compact order-card reply thread (flat avatar + name + text, matches order-detail-card.html).
     * 'calendar' — event-modal chrome (rose-themed avatar + char count).
     * 'modal' — same flat thread layout as 'card', no inner heading (parent already labels the section).
     * 'default' — Segment + List chrome, with an inner "Comments" heading (dining/menu).
     */
    variant?: 'default' | 'modal' | 'card' | 'calendar';
}

export const OrderCommentsSection: React.FC<OrderCommentsSectionProps> = ({
    commentsEnabled,
    facilityId,
    requestInstanceId,
    serviceInstanceId,
    postId,
    targetType,
    registrantId,
    facilityTimezone,
    onCountChange,
    variant = 'default',
}) => {
    const activeResident = useActiveResident();
    const ownAvatarImage = getResidentDisplayImage(activeResident);
    const ownFirstName = getResidentDisplayFirstName(activeResident);
    const ownLastName = getResidentDisplayLastName(activeResident);
    const ownFullName = [ownFirstName, ownLastName].filter(Boolean).join(' ').trim();
    const [comments, setComments] = useState<Comment[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [addText, setAddText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [commentToDelete, setCommentToDelete] = useState<Comment | null>(null);
    const [commentToReport, setCommentToReport] = useState<Comment | null>(null);
    const [reportSubmitting, setReportSubmitting] = useState(false);
    // Comment ids the server has confirmed as already reported (another user beat us to it).
    // A comment can only be reported once, so its menu shows a disabled "Already reported".
    const [alreadyReportedIds, setAlreadyReportedIds] = useState<Set<string>>(new Set());
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editText, setEditText] = useState('');
    const [commentImageFile, setCommentImageFile] = useState<File | null>(null);
    const [commentImagePreview, setCommentImagePreview] = useState('');
    const [commentImageError, setCommentImageError] = useState('');
    const resolvedTargetType: 'requestInstance' | 'serviceInstance' | 'post' =
        targetType || (postId ? 'post' : serviceInstanceId ? 'serviceInstance' : 'requestInstance');
    const resolvedRequestInstanceId = resolvedTargetType === 'requestInstance' ? requestInstanceId : undefined;
    const resolvedServiceInstanceId = resolvedTargetType === 'serviceInstance' ? serviceInstanceId : undefined;
    const resolvedPostId = resolvedTargetType === 'post' ? postId : undefined;
    const hasValidTarget = resolvedTargetType === 'post'
        ? Boolean(resolvedPostId)
        : resolvedTargetType === 'requestInstance'
            ? Boolean(resolvedRequestInstanceId)
            : Boolean(resolvedServiceInstanceId);
    const isCalendar = variant === 'calendar';
    const isCard = variant === 'card';
    const isModal = variant === 'modal';

    // The parent only mounts this section while its thread is expanded (e.g.
    // OrderStatusCard's "Comments" toggle), so collapsing it mid-request is a
    // normal interaction, not an edge case — guard every state update below.
    const isMountedRef = useRef(true);
    useEffect(() => () => { isMountedRef.current = false; }, []);

    // Mirrors `comments` synchronously so loadComments can append to the latest
    // list without taking a stale closure over it.
    const commentsRef = useRef<Comment[]>([]);
    useEffect(() => { commentsRef.current = comments; }, [comments]);

    const isPost = resolvedTargetType === 'post';

    // Post threads render through the same flat chat layout as the order card and the order
    // detail modal. They used to fall through to the `default` Segment/List branch, which is
    // built for dining/menu and carries a second, inline-styled copy of the photo
    // attach/preview/remove widget — two versions of one control to keep in sync. It also
    // printed its own "Comments" heading directly under the post card's own Comment toggle.
    // Keyed off isPost rather than a new variant so any future post surface picks up the
    // same layout without having to remember the prop.
    const isChatLayout = isCard || isModal || isPost;

    // How many comments the last fetch actually returned. Load More grows the window from
    // this, not from a page counter — add/delete/report change the list length without
    // moving a page cursor, which is how the old `pageNo + 1` ended up addressing a range
    // that had already shifted.
    const serverLoadedRef = useRef(0);
    // Comments posted in this session, kept visible until a fetched window actually reaches
    // them. Held separately so pagination offsets stay based on what the server returned.
    const locallyAddedRef = useRef<Comment[]>([]);
    // A photo already pushed to S3 for the comment being composed (see handleAdd).
    const uploadedImageRef = useRef<{ imageUrl: string; imageFileName: string } | null>(null);

    /**
     * Fetch the thread as a single page-1 window of `windowSize` comments and replace what is
     * on screen with it. Every read goes through this one path, so there is no cursor to fall
     * out of step with the list.
     */
    const loadComments = useCallback(async (windowSize?: number, isLoadMore: boolean = false) => {
        if (!hasValidTarget) return;
        const basePageSize = isPost ? POST_PAGE_SIZE : PAGE_SIZE;
        const pageSize = Math.max(windowSize ?? basePageSize, basePageSize);
        if (isLoadMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        try {
            const res = await listComments({
                facilityId,
                registrantId,
                requestInstanceId: resolvedRequestInstanceId,
                serviceInstanceId: resolvedServiceInstanceId,
                postId: resolvedPostId,
                pageNo: 1,
                pageSize,
            });
            if (!isMountedRef.current) return;
            const pageItems = res.Result || [];
            const byId = new Map<string, Comment>();
            pageItems.forEach((c) => byId.set(c._id, c));
            // comments/list returns a post thread newest-first, so page 1 of `pageSize` is
            // the most recent `pageSize` comments. Re-sort that window ascending so it reads
            // oldest -> newest top to bottom; Load More grows it towards older comments.
            const fetched = Array.from(byId.values()).sort((a, b) => (a.DateAdded || 0) - (b.DateAdded || 0));
            const total = res.TotRecords ?? fetched.length;

            // handleAdd splices a just-posted comment in locally without refetching. The next
            // fetch pulls the newest window, which now contains it — so drop it from the
            // carry-over once a fetched page covers it, and clear it entirely once the whole
            // thread is loaded. Without this the comment would render twice for one fetch.
            const reachedEnd = total <= fetched.length;
            locallyAddedRef.current = reachedEnd
                ? []
                : locallyAddedRef.current.filter((c) => !byId.has(c._id));
            const visible = [...fetched, ...locallyAddedRef.current];

            serverLoadedRef.current = fetched.length;
            commentsRef.current = visible;
            setComments(visible);
            setTotalCount(total);
            // Only post threads paginate; request/service-instance threads always
            // fetch everything in one page, so there's never a "more" to reveal.
            setHasMore(isPost ? total > fetched.length : false);
            if (onCountChange) onCountChange(total);
        } catch (e) {
            if (isMountedRef.current) {
                sendToast('error', e instanceof Error ? e.message : 'Failed to load comments');
            }
        } finally {
            if (isMountedRef.current) {
                if (isLoadMore) setLoadingMore(false); else setLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityId, registrantId, resolvedRequestInstanceId, resolvedServiceInstanceId, resolvedPostId, hasValidTarget, isPost]);

    /**
     * Re-read exactly the range currently on screen. Edit, delete and report use this instead
     * of splicing locally, so the freed slot is back-filled from the server and the window
     * stays consistent with what Load More will ask for next.
     */
    const refreshLoadedWindow = useCallback(
        () => loadComments(Math.max(serverLoadedRef.current, isPost ? POST_PAGE_SIZE : PAGE_SIZE)),
        [loadComments, isPost],
    );

    useEffect(() => {
        if (commentsEnabled && hasValidTarget) {
            loadComments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commentsEnabled, hasValidTarget, loadComments]);

    const handleLoadMore = () => {
        if (loading || loadingMore || !hasMore) return;
        // Offset comes from how much the server has actually returned, not from a page
        // counter that mutations can leave behind.
        void loadComments(serverLoadedRef.current + POST_PAGE_SIZE, true);
    };

    const getCommentText = (c: Comment) => c.Text ?? c.text ?? '';

    /**
     * Post threads read like a social feed, so they show relative time ("2 hours ago").
     * Every other target — orders, calendar events, menu items — keeps the absolute
     * facility-local timestamp it has always shown: those threads are a record of when
     * something was said about a specific order, where "3 days ago" loses the point. This
     * is also what `facilityTimezone` is for; a bare moment() renders in the viewer's own
     * clock, which is wrong for a family member in another timezone.
     */
    const formatCommentTime = (comment: Comment): string => {
        if (!comment.DateAdded) return '';
        if (isPost) return moment(comment.DateAdded).fromNow();
        const timezone = (facilityTimezone || '').trim();
        return timezone
            ? formatDateWithTZ(comment.DateAdded, 'MM/DD/YYYY hh:mm A', timezone)
            : moment(comment.DateAdded).format('MM/DD/YYYY hh:mm A');
    };

    const handleAdd = async () => {
        if (submitting) return;
        const text = (addText || '').trim().slice(0, MAX_COMMENT_LENGTH);
        // A photo alone (no text) is a valid comment for any target type.
        const hasImage = Boolean(commentImageFile);
        if (!text && !hasImage) {
            sendToast('warn', 'Comment cannot be empty.');
            return;
        }
        // Validated before touching S3. These checks used to run only inside createComment,
        // i.e. after the upload, so a bad target left the image sitting in the bucket with
        // nothing referencing it and no way to reach it again.
        if (!hasValidTarget || !registrantId) {
            sendToast('error', 'Cannot add a comment here right now.');
            return;
        }
        setSubmitting(true);
        try {
            let uploaded = uploadedImageRef.current;
            if (commentImageFile && !uploaded) {
                const uploadData = await getCommentUploadUrl({ facilityId, fileName: commentImageFile.name });
                const uploadRes = await fetch(uploadData.presignedUrl, {
                    method: 'PUT',
                    body: commentImageFile,
                    headers: { 'Content-Type': commentImageFile.type },
                });
                // fetch only rejects on network failure, so an expired/denied presign
                // resolves fine and would save a comment pointing at a missing object.
                if (!uploadRes.ok) {
                    throw new Error('Failed to upload image. Please try again.');
                }
                // Survives a failed createComment so a retry reuses the object already in
                // S3 instead of uploading a second copy and orphaning the first.
                uploaded = { imageUrl: uploadData.imageUrl, imageFileName: uploadData.fileName };
                uploadedImageRef.current = uploaded;
            }

            const created = await createComment({
                facilityId,
                registrantId,
                targetType: resolvedTargetType,
                requestInstanceId: resolvedRequestInstanceId,
                serviceInstanceId: resolvedServiceInstanceId,
                postId: resolvedPostId,
                text,
                imageUrl: uploaded?.imageUrl,
                imageFileName: uploaded?.imageFileName,
            });
            if (!isMountedRef.current) return;
            setAddText('');
            clearCommentImage();
            uploadedImageRef.current = null;

            // Splice the returned document in at the bottom of the window instead of
            // re-reading: the author sees their comment immediately, and the next fetch
            // reconciles it (the newest window contains it) via locallyAddedRef.
            if (created && created._id) {
                const newComment: Comment = {
                    ...created,
                    // createComment returns the stored document; the display fields are only
                    // resolved by listComments, so fall back to the signed-in resident.
                    AuthorType: created.AuthorType || 'resident',
                    RegistrantId: created.RegistrantId || registrantId,
                };
                locallyAddedRef.current = [
                    ...locallyAddedRef.current.filter((c) => c._id !== newComment._id),
                    newComment,
                ];
                const next = [...commentsRef.current.filter((c) => c._id !== newComment._id), newComment];
                commentsRef.current = next;
                setComments(next);
                const nextTotal = totalCount + 1;
                setTotalCount(nextTotal);
                if (onCountChange) onCountChange(nextTotal);
            } else {
                await refreshLoadedWindow();
            }

            if (created && created.Message) {
                sendToast('info', created.Message);
            } else {
                sendToast('success', 'Comment added.');
            }
        } catch (err) {
            if (isMountedRef.current) sendToast('error', err instanceof Error ? err.message : 'Failed to add comment');
        } finally {
            if (isMountedRef.current) setSubmitting(false);
        }
    };

    const handleCommentImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Reset the input value so choosing the same file again after clearing still fires onChange.
        e.target.value = '';
        if (!file) return;
        if (!ALLOWED_TYPES.includes(file.type)) {
            setCommentImageError('Only JPEG, PNG, GIF, and WebP images are allowed');
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setCommentImageError('Image must be under 5MB');
            return;
        }
        setCommentImageError('');
        // Any object already uploaded belonged to the previous file. Drop the claim so
        // handleAdd re-uploads this one instead of reusing the stale S3 URL after a
        // failed createComment left uploadedImageRef pointing at the old photo.
        uploadedImageRef.current = null;
        setCommentImageFile(file);
        setCommentImagePreview(prev => {
            if (prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return URL.createObjectURL(file);
        });
    };

    const clearCommentImage = () => {
        // Dropping the attachment also drops the claim on anything already uploaded for it,
        // so a later comment never picks up a photo the resident removed.
        uploadedImageRef.current = null;
        setCommentImageFile(null);
        setCommentImagePreview(prev => {
            if (prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return '';
        });
        setCommentImageError('');
    };

    /**
     * Apply a mutation to a comment that is still only in `locallyAddedRef`.
     *
     * Such a comment sits past the end of every fetched window, so refreshLoadedWindow can
     * neither update nor remove it — without this, editing a just-posted comment showed the
     * old text and deleting one left it on screen until the whole thread was loaded.
     * `patch === null` removes it.
     */
    const reconcileLocallyAdded = (commentId: string, patch: Partial<Comment> | null) => {
        locallyAddedRef.current = patch === null
            ? locallyAddedRef.current.filter((c) => c._id !== commentId)
            : locallyAddedRef.current.map((c) => (c._id === commentId ? { ...c, ...patch } : c));
    };

    /**
     * A comment needs text or a photo, not both — the same rule handleAdd applies.
     *
     * The edit form has no image control, so the photo is whatever is already stored: a
     * photo-only comment could not be saved at all while this required text, forcing its
     * author to invent a caption before any edit would go through.
     */
    const canSubmitEdit = (comment: Comment): boolean => Boolean(editText.trim() || comment.ImageUrl);

    const handleUpdate = async (comment: Comment) => {
        if (submitting) return;
        const text = (editText ?? getCommentText(comment)).trim().slice(0, MAX_COMMENT_LENGTH);
        if (!text && !comment.ImageUrl) {
            sendToast('warn', 'Comment cannot be empty.');
            return;
        }
        setSubmitting(true);
        try {
            const result = await updateComment({
                facilityId,
                registrantId,
                commentId: comment._id,
                text,
            });
            if (!isMountedRef.current) return;
            setEditingId(null);
            setEditText('');
            reconcileLocallyAdded(comment._id, { Text: text, text, IsEdited: true });
            await refreshLoadedWindow();
            if (result && result.Message) {
                sendToast('info', result.Message);
            } else {
                sendToast('success', 'Comment updated.');
            }
        } catch (e) {
            if (isMountedRef.current) sendToast('error', e instanceof Error ? e.message : 'Failed to update comment');
        } finally {
            if (isMountedRef.current) setSubmitting(false);
        }
    };

    const handleDelete = async (comment: Comment) => {
        setDeletingId(comment._id);
        try {
            const result = await deleteComment({
                facilityId,
                registrantId,
                commentId: comment._id,
            });
            if (!isMountedRef.current) return;
            reconcileLocallyAdded(comment._id, null);
            await refreshLoadedWindow();
            const msg = result && (result.message || result.Message);
            if (msg) {
                sendToast('success', msg);
            } else {
                sendToast('success', 'Comment deleted.');
            }
            setCommentToDelete(null);
        } catch (e) {
            if (isMountedRef.current) sendToast('error', e instanceof Error ? e.message : 'Failed to delete comment');
        } finally {
            if (isMountedRef.current) setDeletingId(null);
        }
    };

    const onConfirmDelete = async () => {
        if (!commentToDelete) return;
        await handleDelete(commentToDelete);
    };

    const isOwnComment = (comment: Comment): boolean => {
        const authorRegistrantId = (comment.RegistrantId ?? '').trim();
        return Boolean(registrantId && authorRegistrantId === registrantId);
    };

    const handleReport = (comment: Comment) => setCommentToReport(comment);

    const onConfirmReport = async (reason?: string) => {
        if (!commentToReport) return;
        const reportedId = commentToReport._id;
        setReportSubmitting(true);
        try {
            const res = await reportContent({ facilityId, contentType: 'Comment', contentId: reportedId, reason, registrantId });
            if (!isMountedRef.current) return;
            setAlreadyReportedIds((prev) => new Set(prev).add(reportedId));
            sendToast(res?.alreadyReported ? 'info' : 'success', res?.alreadyReported ? 'This comment has already been reported' : 'Comment reported');
            reconcileLocallyAdded(reportedId, null);
            setCommentToReport(null);
            // Reported comments are hidden server-side going forward. Re-read the window
            // rather than splicing: a local removal shrank the list without moving the
            // pagination offset, so the next Load More skipped whichever comment had
            // shifted into the freed slot. Same path as delete.
            await refreshLoadedWindow();
        } catch (e) {
            if (isMountedRef.current) sendToast('error', e instanceof Error ? e.message : 'Failed to report comment');
        } finally {
            if (isMountedRef.current) setReportSubmitting(false);
        }
    };

    const isPending = (comment: Comment): boolean => {
        const status = comment.ApprovalStatus ?? '';
        return status.toLowerCase() === 'pending';
    };

    const sideOf = (comment: Comment): 'staff' | 'resident' => (comment.AuthorType === 'resident' ? 'resident' : 'staff');

    const displayFor = (comment: Comment) => {
        const mine = isOwnComment(comment);
        // For the resident's own comments, show their current identity (matches the
        // site header) instead of whatever name/image the backend snapshotted on that comment.
        const displayName = mine ? (ownFullName || comment.AuthorDisplayName || 'Someone') : (comment.AuthorDisplayName || 'Someone');
        // Comments only carry a combined display name, not separate first/last —
        // split it so Avatar's initials fallback matches the composer (e.g. "RN").
        const [firstName, ...rest] = displayName.trim().split(/\s+/);
        const lastName = rest.length ? rest[rest.length - 1] : undefined;
        return {
            mine,
            displayName,
            displayImage: mine ? (ownAvatarImage || comment.AuthorImageUrl) : comment.AuthorImageUrl,
            firstName,
            lastName,
        };
    };

    const renderCommentMenu = (comment: Comment) => (
        <Dropdown
            icon={null}
            direction="left"
            className={`link icon${isChatLayout ? ' oc-menu' : ''}`}
            style={{
                color: '#9ca3af',
                cursor: deletingId === comment._id ? 'wait' : 'pointer',
                padding: 4,
            }}
            disabled={submitting || deletingId === comment._id}
            trigger={
                deletingId === comment._id ? (
                    <Loader active inline size="small" style={{ margin: 0 }} />
                ) : (
                    <Icon name="ellipsis vertical" fitted style={{ margin: 0, fontSize: '1rem' }} />
                )
            }
        >
            <Dropdown.Menu>
                <Dropdown.Item
                    text="Edit"
                    icon="pencil"
                    onClick={() => { setEditingId(comment._id); setEditText(getCommentText(comment)); }}
                />
                <Dropdown.Item
                    text="Delete"
                    icon="trash"
                    onClick={() => setCommentToDelete(comment)}
                    style={{ color: '#dc2626' }}
                />
            </Dropdown.Menu>
        </Dropdown>
    );

    const renderReportMenu = (comment: Comment) => {
        const reported = !!comment.IsReported || alreadyReportedIds.has(comment._id);
        return (
            <Dropdown
                icon={null}
                direction="left"
                className={`link icon${isChatLayout ? ' oc-menu' : ''}`}
                style={{ color: '#9ca3af', cursor: 'pointer', padding: 4 }}
                disabled={submitting}
                trigger={<Icon name="ellipsis vertical" fitted style={{ margin: 0, fontSize: '1rem' }} />}
            >
                <Dropdown.Menu>
                    {reported ? (
                        <Dropdown.Item
                            text="Already reported"
                            icon="flag"
                            disabled
                            style={{ color: '#9ca3af', pointerEvents: 'none' }}
                        />
                    ) : (
                        <Dropdown.Item
                            text="Report"
                            icon="flag"
                            onClick={() => handleReport(comment)}
                            style={{ color: '#f2711c' }}
                        />
                    )}
                </Dropdown.Menu>
            </Dropdown>
        );
    };

    const renderEditForm = (comment: Comment) => (
        // A plain div, not <Form>: this renders inside the order-detail page's own
        // outer <Form onSubmit={handleSubmit}> (see CreateServiceOrder.tsx), and
        // nested <form> elements are invalid HTML — the browser flattens them into
        // the outer form, so a <button> without an explicit type here would submit
        // (and re-create) that outer order instead of just editing this comment.
        <div style={{ marginBottom: 0, width: '100%' }}>
            {isChatLayout ? (
                <>
                    <TextArea
                        className="detail-comment-textarea"
                        value={editText}
                        onChange={(_, { value }) => setEditText(String(value ?? '').slice(0, MAX_COMMENT_LENGTH))}
                        placeholder={`Edit your comment... (${MAX_COMMENT_LENGTH} chars max)`}
                        maxLength={MAX_COMMENT_LENGTH}
                        rows={2}
                        style={{ marginBottom: 8 }}
                    />
                    <div className="detail-comment-edit-actions">
                        <Button type="button" className="detail-comment-submit" onClick={() => handleUpdate(comment)} loading={submitting} disabled={submitting || !canSubmitEdit(comment)}>
                            Update
                        </Button>
                        <Button type="button" className="detail-comment-cancel" onClick={() => { setEditingId(null); setEditText(''); }} disabled={submitting}>
                            Cancel
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <Form.Field style={{ marginBottom: 10, width: '100%' }}>
                        <TextArea
                            value={editText}
                            onChange={(_, { value }) => setEditText(String(value ?? '').slice(0, MAX_COMMENT_LENGTH))}
                            placeholder={`Edit your comment... (${MAX_COMMENT_LENGTH} chars max)`}
                            maxLength={MAX_COMMENT_LENGTH}
                            rows={COMMENT_TEXTAREA_ROWS}
                            style={COMMENT_TEXTAREA_STYLE}
                        />
                    </Form.Field>
                    <Button type="button" size="small" primary onClick={() => handleUpdate(comment)} loading={submitting} disabled={submitting || !canSubmitEdit(comment)}>
                        Update
                    </Button>
                    <Button type="button" size="small" onClick={() => { setEditingId(null); setEditText(''); }} disabled={submitting}>
                        Cancel
                    </Button>
                </>
            )}
        </div>
    );

    // Enter-to-send only makes sense for the compact card/modal thread (matches
    // chat-style UX). Calendar/dining/default use a multi-line textarea where
    // Enter should insert a newline, as it always has.
    //
    // Deliberately still isCard/isModal and not isChatLayout: post threads moved into that
    // layout but their composer is long-form, so Enter keeps inserting a newline there
    // rather than silently posting a half-written comment.
    const renderAddKeyDown = (ev: React.KeyboardEvent) => {
        if (!isCard && !isModal) return;
        if (ev.key === 'Enter' && !ev.shiftKey) {
            ev.preventDefault();
            ev.stopPropagation();
            handleAdd();
        }
    };

    if (!commentsEnabled || !hasValidTarget) return null;

    const remainingCount = Math.max(totalCount - comments.length, 0);
    const loadMoreCount = Math.min(remainingCount, POST_PAGE_SIZE);
    const loadMoreLabel = loadMoreCount > 0
        ? `Load ${loadMoreCount} earlier comment${loadMoreCount === 1 ? '' : 's'}`
        : 'Load earlier comments';

    const addPlaceholder = isCalendar
        ? 'Add a comment...'
        : `Add a comment... (${MAX_COMMENT_LENGTH} chars max)`;

    const deleteModal = (
        <DeleteConfirmationModal
            open={Boolean(commentToDelete)}
            onClose={() => setCommentToDelete(null)}
            onConfirm={onConfirmDelete}
            messageToDisplay="Are you sure you want to delete this comment?"
            loading={commentToDelete !== null && deletingId === commentToDelete._id}
        />
    );

    // Card (order-list inline thread), modal (order-detail) and post threads all use the
    // same flat avatar + name + text layout (matches order-detail-card.html) — only the
    // "Comments" heading placement differs, and that's handled by the parent (each already
    // labels the section before mounting this: the order card's Comments toggle, the post
    // card's Comment toggle).
    const reportModal = (
        <ReportContentModal
            open={Boolean(commentToReport)}
            onClose={() => setCommentToReport(null)}
            onSubmit={onConfirmReport}
            loading={reportSubmitting}
            contentLabel="comment"
        />
    );

    if (isChatLayout) {
        return (
            <div className="oc-wrapper oc-wrapper--card">
                {/* Above the list: the window holds the newest comments and grows towards
                    older ones, so this control reveals rows that appear at the top. Only
                    post threads paginate; order threads fetch everything in one page. */}
                {isPost && !loading && hasMore && (
                    <LoadMore isLoading={loadingMore} hasMore={hasMore} next={handleLoadMore} skipMargin label={loadMoreLabel} />
                )}
                {loading ? (
                    <div className="oc-loading">
                        <Loader active inline="centered" size="small" />
                    </div>
                ) : (
                    <div className="detail-comments-list">
                        {comments.length === 0 ? (
                            <div className="detail-comments-empty">No comments yet.</div>
                        ) : (
                            comments.map((c) => {
                                const side = sideOf(c);
                                const { displayName, displayImage, firstName, lastName } = displayFor(c);
                                return (
                                    <div key={c._id} className="detail-comment">
                                        {/* "ui avatar image" replicates the classes Semantic's <Image avatar> used to
                                            render, which the .detail-comment-avatar.ui.image.avatar border-color rules
                                            below key off — Avatar itself doesn't add them. */}
                                        <Avatar
                                            src={displayImage}
                                            firstName={firstName}
                                            lastName={lastName}
                                            className={`ui avatar image detail-comment-avatar ${side}`}
                                        />
                                        {editingId === c._id ? (
                                            <div className="detail-comment-edit-body">
                                                {renderEditForm(c)}
                                            </div>
                                        ) : (
                                            <div className="detail-comment-row">
                                                <div className="detail-comment-body">
                                                    <div className="detail-comment-header">
                                                        <span className="detail-comment-name">{displayName}</span>
                                                        <span className="detail-comment-time">
                                                            {formatCommentTime(c)}
                                                            {c.IsEdited && ' (edited)'}
                                                        </span>
                                                        {isPending(c) && (
                                                            <Label color="yellow" size="tiny">Pending Approval</Label>
                                                        )}
                                                    </div>
                                                    <div className="detail-comment-text">{getCommentText(c)}</div>
                                                    {c.ImageUrl && (
                                                        <div className="detail-comment-image">
                                                            <img
                                                                src={c.ImageUrl}
                                                                alt="Comment attachment"
                                                                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                                                    const wrapper = e.currentTarget.parentElement;
                                                                    if (wrapper) wrapper.style.display = 'none';
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                {isOwnComment(c) ? renderCommentMenu(c) : renderReportMenu(c)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
                <div className="detail-comment-input-row">
                    {/* Avatar fills its parent (its initials fallback is width/height 100%),
                        so the 32px size lives on this wrapper, not on Avatar itself. */}
                    <div className="detail-comment-avatar-input">
                        <Avatar
                            src={ownAvatarImage}
                            firstName={ownFirstName}
                            lastName={ownLastName}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <TextArea
                            className="detail-comment-textarea"
                            placeholder={addPlaceholder}
                            value={addText}
                            onChange={(_, { value }) => setAddText(String(value ?? '').slice(0, MAX_COMMENT_LENGTH))}
                            onKeyDown={renderAddKeyDown}
                            maxLength={MAX_COMMENT_LENGTH}
                            rows={2}
                        />
                        <div className="detail-comment-photo-row">
                            <input
                                type="file"
                                id={`comment-image-input-${variant}-${resolvedPostId || resolvedServiceInstanceId || resolvedRequestInstanceId || 'generic'}`}
                                className="detail-comment-photo-input"
                                accept={ALLOWED_TYPES.join(',')}
                                onChange={handleCommentImageSelect}
                                disabled={submitting}
                            />
                            {commentImagePreview ? (
                                <div className="detail-comment-photo-preview">
                                    <img src={commentImagePreview} alt="Attachment preview" />
                                    <label
                                        htmlFor={`comment-image-input-${variant}-${resolvedPostId || resolvedServiceInstanceId || resolvedRequestInstanceId || 'generic'}`}
                                        className="detail-comment-photo-change"
                                        style={{ cursor: submitting ? 'not-allowed' : 'pointer' }}
                                    >
                                        Change photo
                                    </label>
                                    <Button
                                        icon="close"
                                        size="mini"
                                        basic
                                        circular
                                        type="button"
                                        className="detail-comment-photo-remove"
                                        onClick={clearCommentImage}
                                        disabled={submitting}
                                    />
                                </div>
                            ) : (
                                <label
                                    htmlFor={`comment-image-input-${variant}-${resolvedPostId || resolvedServiceInstanceId || resolvedRequestInstanceId || 'generic'}`}
                                    className="detail-comment-photo-attach"
                                    style={{ cursor: submitting ? 'not-allowed' : 'pointer' }}
                                >
                                    <Icon name="image outline" />
                                    Attach photo
                                </label>
                            )}
                            {commentImageError && (
                                <div className="detail-comment-photo-error">{commentImageError}</div>
                            )}
                        </div>
                        <Button
                            type="button"
                            className="detail-comment-submit"
                            loading={submitting}
                            disabled={submitting || (!addText.trim() && !commentImageFile)}
                            onClick={() => handleAdd()}
                        >
                            Add comment
                        </Button>
                    </div>
                </div>
                {deleteModal}
                {reportModal}
            </div>
        );
    }

    return (
        <Segment
            className={isCalendar ? 'cal-evt-comments-inner' : undefined}
            style={
                isCalendar
                    ? { borderRadius: 0, boxShadow: 'none', padding: 0, marginTop: 0 }
                    : { borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.06)', padding: '1.25rem', marginTop: '1rem' }
            }
        >
            <h4 style={isCalendar ? { margin: '0 0 12px 0', fontSize: 16, fontWeight: 700 } : { margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: 600 }}>Comments</h4>
            {/* No Load More here: this branch is dining/menu/calendar only now, and those
                threads always come back in a single page (hasMore stays false). */}
            {loading ? (
                <div style={{ padding: '1.5rem 0', textAlign: 'center' }}>
                    <Loader active inline="centered" size="small" />
                </div>
            ) : (
                <List relaxed divided style={{ marginBottom: '1.25rem', border: 'none' }}>
                    {comments.length === 0 ? (
                        <List.Item style={{ color: '#6b7280', padding: '0.75rem 0', fontStyle: 'italic' }}>
                            No comments yet.
                        </List.Item>
                    ) : (
                        comments.map((c) => {
                            const { displayName, displayImage, firstName, lastName } = displayFor(c);
                            return (
                                <List.Item key={c._id} style={{ padding: '0.875rem 0', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'flex-start' }}>
                                    <Avatar
                                        src={displayImage}
                                        firstName={firstName}
                                        lastName={lastName}
                                        className="oc-list-avatar"
                                    />
                                    {editingId === c._id ? (
                                        <List.Content style={{ flex: 1, marginLeft: 12, minWidth: 0, width: '100%' }}>
                                            {renderEditForm(c)}
                                        </List.Content>
                                    ) : (
                                        <List.Content style={{ position: 'relative', flex: 1, marginLeft: 12, minWidth: 0, paddingRight: 32 }}>
                                            <List.Description style={{ color: '#6b7280', fontSize: '0.8125rem', marginBottom: 4, minHeight: 28 }}>
                                                <strong style={{ color: '#374151' }}>{displayName}</strong>
                                                {' · '}
                                                {formatCommentTime(c)}
                                                {c.IsEdited && ' (edited)'}
                                                {isPending(c) && (
                                                    <>
                                                        {' '}
                                                        <Label color="yellow" size="tiny">Pending Approval</Label>
                                                    </>
                                                )}
                                            </List.Description>
                                            <p style={{ margin: 0, fontSize: '0.9375rem', lineHeight: 1.45, textAlign: 'left' }}>{getCommentText(c)}</p>
                                            {c.ImageUrl && (
                                                <div
                                                    style={{
                                                        display: 'inline-block',
                                                        marginTop: 8,
                                                        padding: 4,
                                                        background: '#f9fafb',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: 8,
                                                    }}
                                                >
                                                    <img
                                                        src={c.ImageUrl}
                                                        alt="Comment attachment"
                                                        style={{ display: 'block', maxWidth: 180, maxHeight: 140, borderRadius: 6, objectFit: 'contain' }}
                                                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                                            const wrapper = e.currentTarget.parentElement;
                                                            if (wrapper) wrapper.style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            <div style={{ position: 'absolute', top: 0, right: 0, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                {isOwnComment(c) ? renderCommentMenu(c) : renderReportMenu(c)}
                                            </div>
                                        </List.Content>
                                    )}
                                </List.Item>
                            );
                        })
                    )}
                </List>
            )}
            <div style={{
                marginBottom: 0,
                paddingTop: isCalendar ? 0 : '1rem',
                borderTop: isCalendar ? undefined : '1px solid #f3f4f6',
            }}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    {isCalendar ? (
                        <div className="cal-evt-comment-avatar-input">
                            <Avatar src={ownAvatarImage} firstName={ownFirstName} lastName={ownLastName} />
                        </div>
                    ) : (
                        <Avatar
                            src={ownAvatarImage}
                            firstName={ownFirstName}
                            lastName={ownLastName}
                            className="oc-list-avatar"
                        />
                    )}
                    <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                        <Form.Field style={{ marginBottom: isCalendar ? 4 : 10, width: '100%' }}>
                            <TextArea
                                placeholder={addPlaceholder}
                                value={addText}
                                onChange={(_, { value }) => setAddText(String(value ?? '').slice(0, MAX_COMMENT_LENGTH))}
                                onKeyDown={renderAddKeyDown}
                                maxLength={MAX_COMMENT_LENGTH}
                                rows={isCalendar ? 2 : COMMENT_TEXTAREA_ROWS}
                                style={isCalendar ? { ...COMMENT_TEXTAREA_STYLE, minHeight: 56 } : COMMENT_TEXTAREA_STYLE}
                            />
                        </Form.Field>
                        {isCalendar && (
                            <div className="cal-evt-comment-char-count">{addText.length} / {MAX_COMMENT_LENGTH}</div>
                        )}
                        <div style={{ marginBottom: 12 }}>
                            <input
                                type="file"
                                id={`comment-image-input-${variant}-${resolvedPostId || resolvedServiceInstanceId || resolvedRequestInstanceId || 'generic'}`}
                                accept={ALLOWED_TYPES.join(',')}
                                onChange={handleCommentImageSelect}
                                disabled={submitting}
                                style={{ display: 'none' }}
                            />
                            {commentImagePreview ? (
                                <div style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: 6,
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    background: '#f9fafb',
                                }}
                                >
                                    <img
                                        src={commentImagePreview}
                                        alt="Attachment preview"
                                        style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                                    />
                                    <label
                                        htmlFor={`comment-image-input-${variant}-${resolvedPostId || resolvedServiceInstanceId || resolvedRequestInstanceId || 'generic'}`}
                                        style={{
                                            cursor: submitting ? 'not-allowed' : 'pointer',
                                            color: '#1991EB',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        Change photo
                                    </label>
                                    <Button
                                        icon="close"
                                        size="mini"
                                        basic
                                        circular
                                        type="button"
                                        onClick={clearCommentImage}
                                        disabled={submitting}
                                        style={{ padding: 6, margin: 0 }}
                                    />
                                </div>
                            ) : (
                                <label
                                    htmlFor={`comment-image-input-${variant}-${resolvedPostId || resolvedServiceInstanceId || resolvedRequestInstanceId || 'generic'}`}
                                    style={{
                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                        color: '#1991EB',
                                        fontSize: '0.9rem',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                    }}
                                >
                                    <Icon name="image outline" />
                                    Attach photo
                                </label>
                            )}
                            {commentImageError && (
                                <div style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 4 }}>{commentImageError}</div>
                            )}
                        </div>
                        <Button
                            type="button"
                            primary
                            size="small"
                            loading={submitting}
                            disabled={submitting || (!addText.trim() && !commentImageFile)}
                            onClick={() => handleAdd()}
                        >
                            Add comment
                        </Button>
                    </div>
                </div>
            </div>
            {deleteModal}
            {reportModal}
        </Segment>
    );
};

export default OrderCommentsSection;
